/**
 * LumosityWatchlistCard — live default-watchlist tickers for a contact,
 * fetched from lumosity_infra (prod app data, not the Amplitude BQ summary).
 *
 * Only render when LUMOSITY_ENABLED (api/lumosity.js) — it uses Clerk hooks,
 * which require the <ClerkProvider> that main.jsx mounts conditionally.
 */

import { useState, useEffect, useCallback } from 'react';
import { useLumosityAuth } from '../auth/useLumosityAuth.js';
import { fetchUserWatchlistInfo } from '../api/lumosity.js';

export default function LumosityWatchlistCard({ email }) {
  const { clerkLoaded, clerkSignedIn, getClerkToken, signInToLumosity } = useLumosityAuth();
  const [state, setState] = useState({ status: 'idle', tickers: [], sector: null, sectorPack: null, error: null });
  const [expanded, setExpanded] = useState(false);

  const load = useCallback(async () => {
    setState({ status: 'loading', tickers: [], sector: null, sectorPack: null, error: null });
    try {
      const { tickers, sector, sectorPack } = await fetchUserWatchlistInfo(getClerkToken, email);
      setState({ status: 'done', tickers, sector, sectorPack, error: null });
    } catch (e) {
      setState({ status: 'error', tickers: [], sector: null, sectorPack: null, error: e.message });
    }
  }, [getClerkToken, email]);

  useEffect(() => {
    if (clerkLoaded && clerkSignedIn && email) load();
  }, [clerkLoaded, clerkSignedIn, email, load]);

  if (!clerkLoaded) return null;

  return (
    <div style={{ background: '#f8fafc', borderRadius: 8, padding: '12px 14px', marginBottom: 24 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 8 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: 'var(--c-text-2)', textTransform: 'uppercase', letterSpacing: '.4px' }}>
          Lumosity Watchlist
        </div>
        {clerkSignedIn && state.status !== 'loading' && (
          <button
            onClick={load}
            title="Refresh from Lumosity"
            style={{ background: 'transparent', border: '1px solid var(--c-border)', borderRadius: 6, padding: '2px 7px', cursor: 'pointer', fontSize: 12, color: 'var(--c-text-2)' }}
          >
            ↻
          </button>
        )}
      </div>

      {!clerkSignedIn ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <span style={{ fontSize: 13, color: 'var(--c-text-3)' }}>
            Sign in to Lumosity to see this contact's live watchlist.
          </span>
          <button
            onClick={signInToLumosity}
            style={{ background: 'var(--c-primary)', color: '#fff', border: 'none', borderRadius: 6, padding: '4px 10px', cursor: 'pointer', fontSize: 12, fontWeight: 600 }}
          >
            Sign in
          </button>
        </div>
      ) : state.status === 'loading' ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, color: 'var(--c-text-3)', fontSize: 13 }}>
          <div className="spinner" style={{ width: 14, height: 14 }} />
          Loading watchlist…
        </div>
      ) : state.status === 'error' ? (
        <div style={{ fontSize: 13, color: 'var(--c-danger, #dc2626)' }}>{state.error}</div>
      ) : state.tickers.length === 0 ? (
        <div style={{ fontSize: 13, color: 'var(--c-text-3)' }}>No default watchlist.</div>
      ) : (
        <div>
          {/* Overall sector derived server-side from the watchlist (IMP-1843) */}
          <div style={{ fontSize: 12, color: 'var(--c-text-2)', marginBottom: 8 }}>
            Sector:{' '}
            <span style={{ fontWeight: 600, color: 'var(--c-text-1, #0f172a)' }}>
              {state.sector ?? 'No dominant sector'}
            </span>
            {state.sectorPack && (
              <span style={{ marginLeft: 6, fontSize: 11, fontWeight: 600, padding: '1px 6px', borderRadius: 4, background: 'var(--c-primary)', color: '#fff' }}>
                {state.sectorPack}
              </span>
            )}
          </div>
          <div
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              gap: 6,
              maxHeight: expanded ? 'none' : 24,
              overflow: 'hidden',
            }}
          >
            {state.tickers.map((t) => (
              <span
                key={t}
                style={{ fontSize: 12, fontWeight: 600, padding: '2px 8px', borderRadius: 5, background: '#fff', border: '1px solid var(--c-border)', color: 'var(--c-text-1, #0f172a)' }}
              >
                {t}
              </span>
            ))}
          </div>
          <button
            onClick={() => setExpanded((v) => !v)}
            style={{ fontSize: 12, color: 'var(--c-text-3)', marginTop: 6, background: 'transparent', border: 'none', cursor: 'pointer', padding: 0, textDecoration: 'underline' }}
          >
            {expanded ? 'Show less' : `${state.tickers.length} tickers · show all`}
          </button>
        </div>
      )}
    </div>
  );
}
